import { isResource } from '@medplum/core'
import type {
  Basic,
  CodeableConcept,
  Coding,
  Patient,
  Practitioner,
  Reference,
  ServiceRequest,
  Task,
} from '@medplum/fhirtypes'
import { compact, orderBy, uniqBy } from 'lodash-es'
import { match } from 'ts-pattern'
import { z } from 'zod'
import {
  codeableConcept,
  codeFromCodeableConcept,
  isSubsetOfCodeableConcepts,
} from '#src/codeableConcept.ts'
import {
  EXTENDED_RESOURCE_SUBSCRIBERS,
  STANDARD_TASK_BUSINESS_STATUS,
  STANDARD_TASK_LABEL_TAG_SYSTEM,
  STANDARD_TASK_SEND_INBOX_NOTIFICATION_EXTENSION,
} from '#src/codeSystems.ts'
import { Extension, Tag } from '#src/decorators.ts'
import type { ExportFields } from '#src/ExtendedResource.ts'
import { getRoleConcepts } from '#src/models/care-teams/index.ts'
import { LEAD_TAG } from '#src/models/patients/index.ts'
import type { Subscribeable } from '#src/models/subscribeable.ts'
import {
  IGNORE_TASK_CODES,
  TASK_CATEGORIES,
} from '#src/models/tasks/constants.ts'
import { ExtendedTask } from '#src/models/tasks/task.ts'
import { getReferenceString, resolveId } from '#src/references.ts'
import { ROLES, type Role } from '#src/roles.ts'
import { type QueryTypes, queryTypesToString } from '#src/search.ts'
import { isOfType, now, parseReferenceString } from '#src/utils.ts'

export type StandardTaskParams = Partial<
  Omit<Task, 'resourceType' | 'owner' | 'performerType'>
> & {
  owner?: Reference<Practitioner>
  roles?: Role[]
  sendInboxNotification?: boolean
}

/**
 * System-generated subtypes must call `getTaskLabelForTask()` and add the
 * result to `task.labels` before they are saved. Generic user-authored tasks
 * are exempt.
 */
export class StandardTask
  extends ExtendedTask<z.infer<typeof StandardTask.stateSchema>>
  implements Subscribeable
{
  static defaultRoles: Role[] = []

  get defaultRoles(): Role[] {
    return (this.constructor as typeof StandardTask).defaultRoles
  }

  // Opt in only for tasks that coordinate the ordered service itself, where the
  // service line legitimately decides who acts. Tasks whose defaultRoles are a
  // hard requirement must stay opted out.
  static useServiceCategoryRoles = false

  get useServiceCategoryRoles(): boolean {
    return (this.constructor as typeof StandardTask).useServiceCategoryRoles
  }

  constructor({
    owner,
    roles,
    sendInboxNotification,
    restriction: { recipient: existingRecipients = [], ...restriction } = {},
    ...rest
  }: StandardTaskParams) {
    super({
      code: StandardTask.code,
      ...mapStandardTaskStateToTaskFields('todo'),
      restriction: {
        recipient: compact([...existingRecipients, owner]),
        ...restriction,
      },

      ...rest,
    })
    this.ownerPractitioner = owner
    this.roles = roles ?? this.defaultRoles
    this.sendInboxNotification = sendInboxNotification
  }

  static stateSchema = z.enum([
    'todo',
    'in-progress',
    'done',
    'backlog',
    'canceled',
    'duplicate',
  ])

  override mapStateToTaskFields(state: StandardTask['state']): {
    businessStatus: CodeableConcept
    intent: StandardTaskIntent
    status: StandardTaskStatus
  } {
    return mapStandardTaskStateToTaskFields(state)
  }

  get state(): z.infer<typeof StandardTask.stateSchema> {
    return StandardTask.stateSchema.parse(
      codeFromCodeableConcept(this.resource.businessStatus)
    )
  }

  static stateText(state: StandardTask['state']): string {
    return match(state)
      .with('todo', () => 'Not Started')
      .with('in-progress', () => 'In Progress')
      .with('done', () => 'Done')
      .with('backlog', () => 'Backlog')
      .with('canceled', () => 'Canceled')
      .with('duplicate', () => 'Duplicate')
      .exhaustive()
  }

  get stateText(): string {
    return StandardTask.stateText(this.state)
  }

  get stateOrder(): number {
    return match(this.state)
      .with('in-progress', () => 0)
      .with('todo', () => 1)
      .with('backlog', () => 2)
      .with('done', () => 3)
      .with('canceled', () => 4)
      .with('duplicate', () => 5)
      .exhaustive()
  }

  get isCompletedOrCancelled(): boolean {
    return StandardTask.isCompletedOrCancelled(this.state)
  }

  get isDone(): boolean {
    return this.state === 'done'
  }

  get isSignedOnBehalf(): boolean {
    return (
      this.isDone &&
      this.ownerPractitioner?.reference !== this.recipient?.reference
    )
  }

  get isCanceled(): boolean {
    return this.state === 'canceled'
  }

  static isCompletedOrCancelled(state: StandardTask['state']): boolean {
    return state === 'done' || state === 'canceled' || state === 'duplicate'
  }

  set state(state) {
    const { businessStatus, intent, status } = this.mapStateToTaskFields(state)

    const isNewState =
      businessStatus !== this.resource.businessStatus ||
      intent !== this.resource.intent ||
      status !== this.resource.status
    if (!isNewState) {
      return
    }

    this.resource.businessStatus = businessStatus
    this.resource.intent = intent
    this.resource.status = status
    this.resource.lastModified = now()
    if (state === 'done') {
      this.completionDate = now()
    }
  }

  markDone(completionDate?: string): void {
    this.state = 'done'
    this.completionDate = completionDate ?? now()
  }

  get roles(): Role[] | undefined {
    return this.resource.performerType
      ?.map((role) => codeFromCodeableConcept(role))
      .filter((role): role is Role => typeof role === 'string' && role in ROLES)
  }

  set roles(roles: Role[] | undefined) {
    this.resource.performerType = roles?.length
      ? getRoleConcepts(roles)
      : undefined
  }

  @Extension({
    url: STANDARD_TASK_SEND_INBOX_NOTIFICATION_EXTENSION,
    kind: 'valueBoolean',
  })
  sendInboxNotification?: boolean

  // The author's explicit choice only applies to the version that created the
  // task; later reassignments fall back to the requester/owner default.
  shouldSendInboxNotification(isNewTask: boolean): boolean {
    if (!this.ownerPractitioner) {
      return false
    }
    if (isNewTask && this.sendInboxNotification !== undefined) {
      return this.sendInboxNotification
    }
    return (
      this.requesterPractitioner?.reference !== this.ownerPractitioner.reference
    )
  }

  @Extension({
    url: EXTENDED_RESOURCE_SUBSCRIBERS,
    kind: 'valueReference',
    allowMultiple: true,
  })
  subscribers_?: Reference<Practitioner>[]

  get subscribers(): Reference<Practitioner>[] {
    return this.subscribers_ ?? []
  }

  set subscribers(subscribers: Reference<Practitioner>[]) {
    if (subscribers.length === 0) {
      this.subscribers_ = undefined
    } else {
      this.subscribers_ = uniqBy(subscribers, (s) => getReferenceString(s))
    }
  }

  addSubscriber(subscriber: Reference<Practitioner>): void {
    this.subscribers = [...this.subscribers, subscriber]
  }

  removeSubscriber(subscriber: Reference<Practitioner>): void {
    this.subscribers = this.subscribers.filter(
      (s) => s.reference !== subscriber.reference
    )
  }

  isSubscriber(practitioner: Reference<Practitioner>): boolean {
    return this.subscribers.some((s) => s.reference === practitioner.reference)
  }

  set ownerPractitioner(owner: Reference<Practitioner> | undefined) {
    super.ownerPractitioner = owner
  }

  get ownerPractitioner(): Reference<Practitioner> | undefined {
    return super.ownerPractitioner
  }

  set requester(requester: Task['requester']) {
    super.requester = requester
  }

  get requester(): Task['requester'] {
    return super.requester
  }

  /**
   * The original recipient of the task.
   * This is used to track the original recipient of the task when the task is
   * reassigned / completed on behalf of the original recipient.
   */
  get recipient(): Reference<Practitioner> | undefined {
    return (
      this.resource.restriction?.recipient?.find((r) =>
        isOfType(r, 'Practitioner')
      ) ?? this.ownerPractitioner
    )
  }

  set recipient(recipient: Reference<Practitioner> | undefined) {
    this.resource.restriction = {
      ...this.resource.restriction,
      recipient: recipient && [recipient],
    }
  }

  @Tag({
    system: STANDARD_TASK_LABEL_TAG_SYSTEM,
    allowMultiple: true,
  })
  _labels?: Coding[]

  get labels(): Reference<Basic>[] {
    return compact(this._labels?.map((l) => parseReferenceString(l.code)))
  }

  set labels(labels: Reference<Basic>[]) {
    if (labels.length === 0) {
      this._labels = undefined
      return
    }
    this._labels = uniqBy(labels, (label) => label.reference).map((l) => ({
      code: l.reference,
      system: STANDARD_TASK_LABEL_TAG_SYSTEM,
    }))
  }

  get actionUrl(): string {
    if (this.participant && isOfType(this.focus, 'ServiceRequest')) {
      return `/Patient/${resolveId(this.participant)}/orders/${resolveId(this.focus)}`
    }

    if (this.participant && isOfType(this.focus, 'CarePlan')) {
      return `/Patient/${resolveId(this.participant)}/care-plans/${resolveId(this.focus)}`
    }

    if (this.participant && isOfType(this.focus, 'DocumentReference')) {
      return `/Patient/${resolveId(this.participant)}/documents`
    }

    return `/tasks/${this.id}`
  }

  static get code(): CodeableConcept {
    return TASK_CATEGORIES.STANDARD_TASK
  }

  override set code(code: Task['code']) {
    super.code = code ?? (this.constructor as typeof StandardTask).code
  }

  override get code(): Task['code'] {
    return super.code
  }

  static isInstance(value: unknown): value is Task {
    return (
      isResource(value) &&
      isOfType(value, 'Task') &&
      isSubsetOfCodeableConcepts(this.code, value.code)
    )
  }

  /**
   * Base FHIR search query for this resource — extended/modified at call sites.
   * Changing this affects every caller. Grep `baseSearchQuery` for impact before editing.
   */
  static baseSearchQuery(opts: QueryTypes = {}): QueryTypes {
    return {
      // Ascending _lastUpdated lets Medplum use cursor-based pagination,
      // avoiding the 10k offset cap on large result sets.
      // https://www.medplum.com/docs/search/paginated-search
      _sort: '_lastUpdated',
      code: this.code,
      'code:not': IGNORE_TASK_CODES,
      'status:not': 'entered-in-error',
      _count: 1000,
      ...opts,
    }
  }

  /**
   * Query for the tasks list page. Excludes done/canceled/duplicate tasks by
   * default to keep the initial fetch bounded — callers that need those
   * states must opt in by passing their own `business-status` or
   * `business-status:not`.
   */
  static listPageSearchQuery(opts: QueryTypes = {}): QueryTypes {
    const hasExplicitBusinessStatus =
      'business-status' in opts || 'business-status:not' in opts
    const businessStatusDefault: QueryTypes = hasExplicitBusinessStatus
      ? {}
      : {
          'business-status:not': [
            StandardTask.stateSchema.enum.done,
            StandardTask.stateSchema.enum.canceled,
            StandardTask.stateSchema.enum.duplicate,
          ],
        }

    return this.baseSearchQuery({
      ...businessStatusDefault,
      ...opts,
    })
  }

  static allParticipantTasks(opts: QueryTypes = {}): QueryTypes {
    return this.baseSearchQuery({
      ...opts,
      'patient:Patient.active': true,
    })
  }

  static allLeadTasks(opts: QueryTypes = {}): QueryTypes {
    return this.baseSearchQuery({
      ...opts,
      'patient:Patient._tag': LEAD_TAG,
    })
  }

  static byFocus(focus: Reference): QueryTypes {
    return {
      'status:not': 'entered-in-error',
      code: this.code,
      focus,
    }
  }

  static byFocuses(focuses: Reference[]): QueryTypes {
    return {
      'status:not': 'entered-in-error',
      code: this.code,
      focus: focuses,
    }
  }

  static activeByFocus(focus: Reference): QueryTypes {
    return {
      ...this.byFocus(focus),
      ...activeQuery,
    }
  }

  static byBasedOn(basedOn: Reference[]): QueryTypes {
    return {
      'status:not': 'entered-in-error',
      code: this.code,
      'based-on': basedOn,
      _sort: '-_lastUpdated',
      _count: 1000,
    }
  }

  static byFocusString(focus: Reference): string {
    return queryTypesToString(this.byFocus(focus))
  }

  static byParticipant(participant: Reference<Patient>): QueryTypes {
    return {
      ...this.baseSearchQuery(),
      patient: participant,
    }
  }

  static activeByParticipant(participant: Reference<Patient>): QueryTypes {
    return {
      ...this.byParticipant(participant),
      ...activeQuery,
    }
  }

  static byOrder(order: Reference<ServiceRequest>): QueryTypes {
    return {
      ...this.baseSearchQuery(),
      focus: order,
    }
  }

  static priorityOrderedTasks(tasks: StandardTask[]): StandardTask[] {
    return orderBy(
      tasks,
      [
        (task) => task.stateOrder,
        (task) => task.priorityNumber ?? 9999, // Tasks without priority go last
        (task) => task.dueOn ?? '9999-12-31', // Tasks without due dates go last
      ],
      ['asc', 'asc']
    )
  }

  protected override getExportFields(): ExportFields {
    return {
      labels: this.labels,
    }
  }
}

type StandardTaskIntent = Extract<Task['intent'], 'proposal' | 'plan' | 'order'>
type StandardTaskStatus = Extract<
  Task['status'],
  'draft' | 'ready' | 'in-progress' | 'completed' | 'cancelled'
>

function mapStandardTaskStateToTaskFields(state: StandardTask['state']): {
  businessStatus: CodeableConcept
  intent: StandardTaskIntent
  status: StandardTaskStatus
} {
  return match<
    StandardTask['state'],
    {
      businessStatus: CodeableConcept
      intent: StandardTaskIntent
      status: StandardTaskStatus
    }
  >(state)
    .with('todo', () => ({
      businessStatus: generateBusinessStatus({
        state: 'todo',
        display: 'Todo',
      }),
      intent: 'plan',
      status: 'ready',
    }))
    .with('in-progress', () => ({
      businessStatus: generateBusinessStatus({
        state: 'in-progress',
        display: 'In Progress',
      }),
      intent: 'order',
      status: 'in-progress',
    }))
    .with('done', () => ({
      businessStatus: generateBusinessStatus({
        state: 'done',
        display: 'Done',
      }),
      intent: 'order',
      status: 'completed',
    }))
    .with('backlog', () => ({
      businessStatus: generateBusinessStatus({
        state: 'backlog',
        display: 'Backlog',
      }),
      intent: 'proposal',
      status: 'draft',
    }))
    .with('canceled', () => ({
      businessStatus: generateBusinessStatus({
        state: 'canceled',
        display: 'Canceled',
      }),
      intent: 'proposal',
      status: 'cancelled',
    }))
    .with('duplicate', () => ({
      businessStatus: generateBusinessStatus({
        state: 'duplicate',
        display: 'Duplicate',
      }),
      intent: 'proposal',
      status: 'cancelled',
    }))
    .exhaustive()
}

function generateBusinessStatus({
  state,
  display,
}: {
  state: StandardTask['state']
  display: string
}): CodeableConcept {
  return codeableConcept({
    system: STANDARD_TASK_BUSINESS_STATUS,
    code: state,
    display,
  })
}

const activeQuery: QueryTypes = {
  'business-status:not': [
    StandardTask.stateSchema.enum.done,
    StandardTask.stateSchema.enum.canceled,
    StandardTask.stateSchema.enum.duplicate,
  ],
}
